import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LiveTrackingMap from '../components/tracking/LiveTrackingMap';
import { TrackShimmer } from '../components/shimmer';
import { bookingsApi, useGetBookingQuery } from '../store/bookingsApi';
import { cartApi } from '../store/cartApi';
import { buildItdoseReportPdfUrl, logReportView } from '../utils/fetchItdoseReports';
import useBookingLocationStream from '../hooks/useBookingLocationStream';
import { useUI } from '../context/UIContext';
import StateCard from '../components/common/StateCard';
import { ROUTES, buildRoute } from '../constants/routes';
import { UI, DISPLAY, EASE } from '../styles/tl5';

// Track — built to the v5 reference.
//
// Header, then a live map beside the sample journey, with the Trustian
// card and booking details in a sidebar.
//
// Two differences from the reference, both in our favour or forced:
//   · Its map is a placeholder grid. Ours is the real LiveTrackingMap on
//     a socket, so the grid only appears when the Trustian is not moving
//     yet (or has finished).
//   · It walks eight invented stages. We use the five our bookings
//     actually have — confirmed, assigned, en route, collected,
//     reported — because those are the statuses the backend sets.
//
// Not built, because there is no field for them: the collection code
// (a security code — inventing one would be actively unsafe) and the
// fasting banner. Both in API-NOTES.

const STAGES = [
  { key: 'confirmed', label: 'Booking confirmed', sub: 'We have your order and your slot.' },
  { key: 'assigned', label: 'Trustian assigned', sub: 'Your collector has been allocated.' },
  { key: 'enroute', label: 'On the way', sub: 'Live location shared below.' },
  { key: 'collected', label: 'Sample collected', sub: 'Sealed and barcoded at your door.' },
  { key: 'reported', label: 'Report ready', sub: 'Doctor-verified and sent to WhatsApp.' },
];

const STATUS_TO_INDEX = { confirmed: 0, assigned: 1, enroute: 2, collected: 3, reported: 4 };

const STATUS_TITLE = {
  confirmed: 'Booking confirmed',
  assigned: 'Your Trustian has been assigned',
  enroute: 'Your Trustian is on the way',
  collected: 'Sample collected',
  reported: 'Your report is ready',
};

const STATUS_SUB = {
  confirmed: 'We’ll assign a Trustian shortly.',
  assigned: 'They set out about 30 minutes before your slot.',
  enroute: 'Follow them on the map below.',
  collected: 'Sealed in front of you and on its way to the lab.',
  reported: 'Doctor-verified and sent to your WhatsApp.',
};

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
};

const card = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const cardTitle = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(24px, 3vw, 36px)',
  lineHeight: 1.05,
  letterSpacing: '-.02em',
  color: 'var(--tl-g9)',
};

const kvRow = { display: 'flex', justifyContent: 'space-between', gap: 1.5, fontFamily: UI, fontSize: 14 };

const Notice = ({ title, body }) => (
  <Box sx={{ maxWidth: 640, mx: 'auto', px: 3, py: 10, textAlign: 'center' }}>
    <Typography component="h1" sx={{ ...cardTitle, fontSize: 32, mb: 1.5 }}>{title}</Typography>
    <Typography sx={{ fontFamily: UI, fontSize: 16, color: 'var(--tl-muted)', mb: 3 }}>{body}</Typography>
    <Box
      component={Link}
      to={ROUTES.DASHBOARD}
      sx={{
        display: 'inline-block', p: '15px 24px', borderRadius: '16px',
        bgcolor: 'var(--tl-g9)', color: '#fff', fontFamily: UI, fontWeight: 800,
        fontSize: 16, textDecoration: 'none',
      }}
    >
      Go to my bookings
    </Box>
  </Box>
);

const Track = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get('id');
  const justPaid = searchParams.get('payment') === 'success';

  const dispatch = useDispatch();
  const { showToast } = useUI();
  const [lookup, setLookup] = useState('');

  const { data, isLoading, isError, error } = useGetBookingQuery(id, { skip: !id });
  const booking = data?.booking;

  // Coming back from PayU success: drop the cart badge, refresh the
  // bookings list, surface a toast, and strip the query so a refresh
  // does not re-trigger it.
  useEffect(() => {
    if (!justPaid) return;
    dispatch(cartApi.util.invalidateTags(['Cart']));
    dispatch(bookingsApi.util.invalidateTags(['Booking']));
    showToast('Payment successful — your booking is confirmed!');
    const next = new URLSearchParams(searchParams);
    next.delete('payment');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackingEnabled = ['assigned', 'enroute'].includes(booking?.status);

  const initialTrustianLocation = useMemo(() => {
    const t = booking?.trustian_location;
    return t && typeof t.lat === 'number' && typeof t.lng === 'number' ? { lat: t.lat, lng: t.lng } : null;
  }, [booking]);

  const { location: liveLocation, isLive } = useBookingLocationStream({
    bookingId: booking?.booking_id,
    initialLocation: initialTrustianLocation,
    enabled: trackingEnabled,
  });

  const patientLocation = useMemo(() => {
    const p = booking?.patient?.location;
    return p && typeof p.lat === 'number' && typeof p.lng === 'number' ? { lat: p.lat, lng: p.lng } : null;
  }, [booking]);

  const stageIdx = booking ? (STATUS_TO_INDEX[booking.status] ?? 0) : 0;

  const onLookup = (e) => {
    e.preventDefault();
    const v = lookup.trim();
    if (v) setSearchParams({ id: v });
  };

  // The lookup box sits in the header on every state, so someone who
  // arrives with no id can still find their booking.
  const lookupBox = (
    <Box
      component="form"
      onSubmit={onLookup}
      sx={{
        display: 'flex',
        gap: 0.75,
        p: 0.625,
        borderRadius: '999px',
        bgcolor: '#FFFFFF',
        flex: '0 1 380px',
        minWidth: 0,
      }}
    >
      <Box
        component="input"
        value={lookup}
        onChange={(e) => setLookup(e.target.value)}
        placeholder="Order ID"
        aria-label="Track by order ID"
        sx={{
          flex: 1, minWidth: 0, border: 0, outline: 0, bgcolor: 'transparent',
          color: 'var(--tl-g9)', fontFamily: UI, fontSize: 15, p: '10px 14px',
        }}
      />
      <Box
        component="button"
        type="submit"
        sx={{
          border: 0, cursor: 'pointer', borderRadius: '999px', p: '10px 16px',
          bgcolor: 'var(--tl-g9)', color: '#FFFFFF',
          fontFamily: UI, fontSize: 14, fontWeight: 800,
        }}
      >
        Track
      </Box>
    </Box>
  );

  if (!id) {
    return (
      <Box sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 4, pb: 9 }}>
        <Box sx={{ maxWidth: 560, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 2.5, textAlign: 'center' }}>
          <Typography component="h1" sx={{ ...cardTitle, fontSize: 'clamp(30px, 4.4vw, 48px)' }}>
            Track your sample
          </Typography>
          <Typography sx={{ fontFamily: UI, fontSize: 16, color: 'var(--tl-muted)' }}>
            Enter your order ID, or pick a booking from your dashboard.
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>{lookupBox}</Box>
          <Box
            component={Link}
            to={ROUTES.DASHBOARD}
            sx={{ fontFamily: UI, fontSize: 15, fontWeight: 800, color: 'var(--tl-teal-d)', textDecoration: 'none' }}
          >
            Go to my bookings →
          </Box>
        </Box>
      </Box>
    );
  }

  if (isLoading) return <TrackShimmer />;

  if (isError || !booking) {
    return <Notice title="Booking not found" body={error?.data?.message || 'We couldn’t find a booking with that reference.'} />;
  }

  if (booking.status === 'cancelled') {
    return (
      <Box sx={{ width: '100%', maxWidth: 680, mx: 'auto', px: 2, pt: 4, pb: 9 }}>
        <StateCard
          icon="✓"
          tone="success"
          title="Booking cancelled"
          body={`Booking ${booking.booking_id} is no longer active. Any payment is refunded to the original method, and reaches your account within 7 business days.`}
          actions={[
            { label: 'Book again', to: ROUTES.BOOKING },
            { label: 'Go home', to: ROUTES.HOME },
          ]}
        />
      </Box>
    );
  }

  // `awaiting_payment` is a real booking status that was missing from
  // STATUS_TO_INDEX, so it fell through to index 0 and told someone whose
  // payment had failed that their booking was confirmed.
  if (booking.status === 'awaiting_payment' || booking.payment_status === 'failed') {
    return (
      <Box sx={{ width: '100%', maxWidth: 680, mx: 'auto', px: 2, pt: 4, pb: 9 }}>
        <StateCard
          icon="!"
          tone="error"
          title="Payment didn’t go through"
          body={`No money was taken for booking ${booking.booking_id}. Your slot is held for a short while — try again, or choose to pay at home.`}
          actions={[
            { label: 'Try again', to: ROUTES.BOOKING },
            { label: 'Get help', to: ROUTES.HELP },
          ]}
        />
      </Box>
    );
  }

  const t = booking.trustian_id;
  const firstItem = booking.items?.[0]?.name;
  const more = (booking.items?.length || 0) - 1;
  const ratingLabel = t && Number(t.rating) > 0 ? Number(t.rating).toFixed(1) : null;

  return (
    <Box component="main" sx={{ width: '100%', maxWidth: 1320, mx: 'auto', px: 2, pt: 1.5, pb: 9 }}>
      {/* ── header ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'end',
          justifyContent: 'space-between', gap: 2, p: '12px 8px 4px',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              fontFamily: UI, fontSize: 13, fontWeight: 800, letterSpacing: '.08em',
              textTransform: 'uppercase', color: 'var(--tl-teal-d)',
            }}
          >
            {`Order #${booking.booking_id}`}
            {firstItem ? ` · ${firstItem}${more > 0 ? ` +${more}` : ''}` : ''}
          </Box>
          <Typography
            component="h1"
            sx={{
              m: 0, fontFamily: DISPLAY, fontWeight: 700,
              fontSize: 'clamp(30px, 4.4vw, 52px)', lineHeight: 0.95,
              letterSpacing: '-.035em', textWrap: 'balance', color: 'var(--tl-g9)',
            }}
          >
            {STATUS_TITLE[booking.status] || 'Tracking your sample'}
          </Typography>
        </Box>
        {lookupBox}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', mt: 1.5 }}>
        {/* ── left ───────────────────────────────────────────────────── */}
        <Box sx={{ flex: '3 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box
            component="section"
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 380,
              borderRadius: '28px',
              overflow: 'hidden',
              // The reference draws a plotting grid; we only fall back to
              // it when there is no Trustian in motion to plot.
              bgcolor: '#D5EFE8',
              backgroundImage: trackingEnabled
                ? 'none'
                : 'linear-gradient(#C3E4DA 1px, transparent 1px), linear-gradient(90deg, #C3E4DA 1px, transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          >
            {trackingEnabled ? (
              <LiveTrackingMap
                patientLocation={patientLocation}
                trustianLocation={liveLocation}
                label={
                  t?.name
                    ? (isLive ? `${t.name} · on the way` : 'Reconnecting…')
                    : (isLive ? 'Trustian on the way' : 'Reconnecting…')
                }
              />
            ) : (
              <Box
                sx={{
                  display: 'flex', flexDirection: 'column', gap: 1,
                  m: 2, p: '22px 26px', maxWidth: 380, textAlign: 'center',
                  borderRadius: '22px', bgcolor: '#FFFFFF',
                }}
              >
                <Box component="span" sx={{ fontFamily: UI, fontSize: 18, fontWeight: 800 }}>
                  {STATUS_TITLE[booking.status]}
                </Box>
                <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.5, color: 'var(--tl-muted-2)' }}>
                  {STATUS_SUB[booking.status]}
                </Box>
              </Box>
            )}
          </Box>

          {/* sample journey */}
          <Box component="section" sx={card}>
            <Typography component="h2" sx={cardTitle}>Sample journey</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {STAGES.map((s, i) => {
                const done = i < stageIdx;
                const now = i === stageIdx;
                const last = i === STAGES.length - 1;
                return (
                  <Box
                    key={s.key}
                    sx={{ display: 'grid', gridTemplateColumns: '36px minmax(0, 1fr)', gap: 1.75, alignItems: 'start' }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                      <Box
                        component="span"
                        sx={{
                          display: 'grid', placeItems: 'center', width: 32, height: 32, flexShrink: 0,
                          borderRadius: '50%',
                          bgcolor: done ? 'var(--tl-teal)' : now ? 'var(--tl-yellow)' : '#EEF3F1',
                          color: done ? '#FFFFFF' : 'var(--tl-g9)',
                          fontFamily: UI, fontSize: 14, fontWeight: 800,
                        }}
                      >
                        {done ? '✓' : String(i + 1)}
                      </Box>
                      {!last && (
                        <Box
                          component="span"
                          sx={{ flex: 1, width: 3, minHeight: 18, bgcolor: done ? 'var(--tl-teal)' : '#E1EBE8' }}
                        />
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', p: '4px 0 18px' }}>
                      <Box
                        component="span"
                        sx={{
                          fontFamily: UI, fontSize: 16, fontWeight: 800,
                          color: done || now ? 'var(--tl-g9)' : '#9AACA7',
                        }}
                      >
                        {s.label}
                      </Box>
                      <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted-2)' }}>
                        {s.sub}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* ── aside ──────────────────────────────────────────────────── */}
        <Box component="aside" sx={{ flex: '1 1 320px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {t && (
            <Box component="section" sx={card}>
              <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'center' }}>
                <Box
                  component="span"
                  sx={{
                    display: 'grid', placeItems: 'center', width: 64, height: 64, flexShrink: 0,
                    borderRadius: '50%', bgcolor: '#D5EFE8',
                    color: 'var(--tl-teal-d)', fontFamily: DISPLAY, fontSize: 22, fontWeight: 700,
                  }}
                >
                  {(t.name || 'T').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('')}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 18, fontWeight: 800 }}>{t.name}</Box>
                  <Box component="span" sx={{ fontFamily: UI, fontSize: 14, color: 'var(--tl-muted-2)' }}>
                    {`Your Trustian${ratingLabel ? ` · ${ratingLabel} ★` : ''}`}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {[t.trustlab_id ? `ID ${t.trustlab_id}` : 'ID verified',
                  'Trained phlebotomist',
                  Number(t.total_jobs) > 0 ? `${t.total_jobs} collections` : null,
                ].filter(Boolean).map((chip) => (
                  <Box
                    key={chip}
                    component="span"
                    sx={{
                      px: '10px', py: '6px', borderRadius: '999px',
                      bgcolor: 'var(--tl-mint)', color: 'var(--tl-teal-d)',
                      fontFamily: UI, fontSize: 12, fontWeight: 800,
                    }}
                  >
                    {chip}
                  </Box>
                ))}
              </Box>

              {t.phone && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  {[
                    { l: 'Call', href: `tel:${String(t.phone).replace(/\s/g, '')}`, bg: 'var(--tl-g9)' },
                    { l: 'WhatsApp', href: `https://wa.me/${String(t.phone).replace(/\D/g, '')}`, bg: 'var(--tl-teal)', ext: true },
                  ].map((a) => (
                    <Box
                      key={a.l}
                      component="a"
                      href={a.href}
                      {...(a.ext ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
                      sx={{
                        display: 'inline-flex', justifyContent: 'center', alignItems: 'center',
                        p: '13px', borderRadius: '16px', bgcolor: a.bg, color: '#FFFFFF',
                        fontFamily: UI, fontSize: 15, fontWeight: 800, textDecoration: 'none',
                        '&:hover': { color: '#FFFFFF', filter: 'brightness(1.08)' },
                      }}
                    >
                      {a.l}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          <Box component="section" sx={{ ...card, gap: 1.25, fontFamily: UI, fontSize: 14 }}>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 16, fontWeight: 800 }}>Booking details</Box>
            {[
              ['Patient', booking.patient?.name || '—'],
              ['Slot', [formatDate(booking.schedule?.date), booking.schedule?.slot].filter(Boolean).join(' · ') || '—'],
              ['Address', booking.patient?.address || '—'],
              ['Payment', booking.payment_method === 'payu' ? 'Paid online' : (booking.payment_method || '—')],
            ].map(([l, v]) => (
              <Box key={l} component="span" sx={kvRow}>
                <Box component="span" sx={{ color: 'var(--tl-muted-2)' }}>{l}</Box>
                <Box component="span" sx={{ fontWeight: 800, textAlign: 'right', minWidth: 0 }}>{v}</Box>
              </Box>
            ))}
            <Box sx={{ display: 'flex', gap: 1, pt: 0.75 }}>
              {[
                { l: 'Reschedule', to: buildRoute(ROUTES.CANCEL_BOOKING, { id: booking.booking_id }) },
                { l: 'Get help', to: ROUTES.HELP },
              ].map((a) => (
                <Box
                  key={a.l}
                  component={Link}
                  to={a.to}
                  sx={{
                    flex: 1, display: 'inline-flex', justifyContent: 'center', alignItems: 'center',
                    p: '11px 14px', borderRadius: '16px', bgcolor: 'var(--tl-mint)', color: 'var(--tl-g9)',
                    fontFamily: UI, fontSize: 14, fontWeight: 800, textDecoration: 'none',
                    transition: `background-color .25s ${EASE}`,
                    '&:hover': { bgcolor: 'var(--tl-mint-2)', color: 'var(--tl-g9)' },
                  }}
                >
                  {a.l}
                </Box>
              ))}
            </Box>
          </Box>

          {booking.itdose?.visit_id && (
            <Box
              component="button"
              type="button"
              onClick={() => {
                logReportView({ visitId: booking.itdose.visit_id, source: 'track-page' });
                window.open(
                  buildItdoseReportPdfUrl({ visitId: booking.itdose.visit_id }),
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
              sx={{
                border: 0, cursor: 'pointer', p: '15px 22px', borderRadius: '16px',
                bgcolor: 'var(--tl-yellow)', color: 'var(--tl-g9)',
                fontFamily: UI, fontSize: 16, fontWeight: 800,
              }}
            >
              Download report (PDF)
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Track;
